import React from 'react';
import {
  FileStatus,
  FileStatusType,
  OrganizationFileResponse,
} from '@server/application/files/types/file.types';
import { CellContext } from '@tanstack/react-table';
import { Progress, Tooltip } from 'antd';
import { FileStatusTooltip } from '@/entities/file/ui/FileStatusTooltip';
import { useKnowledgeBaseFileExplorerContext } from '@/widgets/knowledge-base/KnowledgeBaseFileExplorerContext';

export enum FileProcessingStatus {
  Exception = 'exception',
  Success = 'success',
  Active = 'active',
}

export const getFileProgress = (status: FileStatus) => {
  const steps = ['upload', 'vectorization', 'extraction'];
  const stepColors = steps.map((step) => {
    const stepStatus = status[step as keyof FileStatus];
    if (
      stepStatus.status === FileStatusType.COMPLETED ||
      stepStatus.status === FileStatusType.SKIPPED
    ) {
      return '#1D6AB9'; // Blue for completed and skipped
    } else if (stepStatus.status === FileStatusType.FAILED) {
      return '#FF4D4F'; // Red for failed
    } else if (stepStatus.status === FileStatusType.PENDING) {
      return '#FF9800'; // Orange for pending
    }
    return '#E8E8E8'; // Gray for other states
  });

  let stepsPassed = 0;
  let hasFailure = false;

  steps.forEach((step) => {
    const stepStatus = status[step as keyof FileStatus];
    if (
      stepStatus.status === FileStatusType.COMPLETED ||
      stepStatus.status === FileStatusType.SKIPPED
    ) {
      stepsPassed++;
    } else if (stepStatus.status === FileStatusType.FAILED) {
      hasFailure = true;
    }
  });

  const percent = Math.floor((stepsPassed / steps.length) * 100);

  const progressStatus: FileProcessingStatus = hasFailure
    ? FileProcessingStatus.Exception
    : stepsPassed === steps.length
      ? FileProcessingStatus.Success
      : FileProcessingStatus.Active;

  return {
    percent,
    status: progressStatus,
    stepColors,
  };
};

export const FileExplorerStatusColumn = <TValue,>({
  row: { original: file },
}: CellContext<OrganizationFileResponse, TValue>) => {
  const { isPolling } = useKnowledgeBaseFileExplorerContext();
  const { percent, status, stepColors } = getFileProgress(file.status);

  return (
    <Tooltip
      title={
        <div>
          <FileStatusTooltip status={file.status} />
          {isPolling && status !== FileProcessingStatus.Success && (
            <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
              Checking for updates...
            </div>
          )}
        </div>
      }
      styles={{ root: { maxWidth: 300, padding: 0 }, body: { padding: 0 } }}
    >
      <div>
        <Progress
          steps={3}
          percent={percent}
          status={status}
          strokeColor={stepColors}
        />
        {isPolling && status !== FileProcessingStatus.Success && (
          <div
            style={{
              fontSize: '12px',
              color: '#1890ff',
              marginTop: '4px',
            }}
          >
            Updating...
          </div>
        )}
      </div>
    </Tooltip>
  );
};
